import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { GripVertical, User2, FileText } from 'lucide-react';
import { DragOverlay } from '../KanbanDndContext';
import { CommDragPreview } from '../communication/board/BoardOverlays';
import { Task } from '../../types';

interface AppDragOverlayPortalProps {
  commDragMeta: any;
  activeDragTask: Task | null | undefined;
  initialActiveNodeRectRef: React.MutableRefObject<ClientRect | null>;
}

export const AppDragOverlayPortal: React.FC<AppDragOverlayPortalProps> = ({
  commDragMeta,
  activeDragTask,
  initialActiveNodeRectRef,
}) => {
  const snapCenterToCursorModifier = useCallback(({
    activatorEvent,
    activeNodeRect,
    draggingNodeRect,
    transform,
  }: any) => {
    if (!activatorEvent || !draggingNodeRect) {
      return transform;
    }

    if (!initialActiveNodeRectRef.current && activeNodeRect) {
      initialActiveNodeRectRef.current = {
        left: activeNodeRect.left,
        top: activeNodeRect.top,
        width: activeNodeRect.width,
        height: activeNodeRect.height,
        bottom: activeNodeRect.bottom,
        right: activeNodeRect.right,
      } as any;
    }

    const baseRect = initialActiveNodeRectRef.current || activeNodeRect;
    if (!baseRect) return transform;

    let clientX = 0;
    let clientY = 0;

    if (typeof activatorEvent.clientX === 'number') {
      clientX = activatorEvent.clientX;
      clientY = activatorEvent.clientY;
    } else if (activatorEvent.touches && activatorEvent.touches[0]) {
      clientX = activatorEvent.touches[0].clientX;
      clientY = activatorEvent.touches[0].clientY;
    } else if (activatorEvent.changedTouches && activatorEvent.changedTouches[0]) {
      clientX = activatorEvent.changedTouches[0].clientX;
      clientY = activatorEvent.changedTouches[0].clientY;
    } else {
      return {
        ...transform,
        x: transform.x + (baseRect.width - draggingNodeRect.width) / 2,
        y: transform.y + (baseRect.height - draggingNodeRect.height) / 2,
      };
    }

    const offsetX = clientX - baseRect.left;
    const offsetY = clientY - baseRect.top;

    return {
      ...transform,
      x: transform.x + offsetX - draggingNodeRect.width / 2,
      y: transform.y + offsetY - draggingNodeRect.height / 2,
    };
  }, [initialActiveNodeRectRef]);

  return createPortal(
    <DragOverlay zIndex={2000} dropAnimation={null} modifiers={[snapCenterToCursorModifier]}>
      {commDragMeta ? (
        <CommDragPreview />
      ) : activeDragTask ? (
        <div
          style={{
            transform: 'rotate(-1deg) scale(1.02)',
            pointerEvents: 'none',
            boxShadow: '0 10px 25px -5px rgba(99, 102, 241, 0.4), 0 8px 10px -6px rgba(99, 102, 241, 0.4)',
          }}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600/95 dark:bg-indigo-950/95 border border-indigo-400/30 text-white rounded-full font-semibold text-xs max-w-[220px] select-none"
        >
          <GripVertical size={13} className="text-white/60 shrink-0" />
          {activeDragTask.type === 'contact' || activeDragTask.category === 'Kommunikation' || activeDragTask.projectId?.includes('comm_internal') ? (
            <User2 size={13} className="text-indigo-200 shrink-0" />
          ) : (
            <FileText size={13} className="text-indigo-200 shrink-0" />
          )}
          <span className="truncate tracking-wide">
            {(activeDragTask.type === 'contact' || activeDragTask.category === 'Kommunikation' || activeDragTask.projectId?.includes('comm_internal'))
              ? (activeDragTask.assignee || activeDragTask.name)
              : activeDragTask.name}
          </span>
        </div>
      ) : null}
    </DragOverlay>,
    document.body
  );
};
